// BOT-EDIT: 2025-01-27T10:40:00Z Create web version of SubscriptionContext for invoice management
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase/config';
import { collection, doc, onSnapshot, getDoc, getDocs, setDoc, Timestamp, updateDoc, runTransaction } from 'firebase/firestore';

// 구독 플랜 상수
export const SUBSCRIPTION_PLANS = {
  FREE_TRIAL: 'free_trial',
  CARD_ONLY: 'card_only',     // 🆕 명함 전용 플랜
  STANDARD: 'standard', 
  PRO: 'pro',
  EXPIRED: 'expired'
} as const;

// 플랜별 제한사항
export const PLAN_LIMITS = {
  [SUBSCRIPTION_PLANS.FREE_TRIAL]: {
    maxCards: 1,                    // 명함 1장
    maxInvoicesPerMonth: 3,         // 인보이스 월간 3건
    maxInvoicesPerYear: 3,          // 인보이스 연간 3건 (기존 호환성)
    customDomain: false,            // 커스텀 도메인 X
    unlimitedQR: false,             // QR 제한
    qrLogo: false,                  // QR 로고 기능 X
    basicTemplate: true,            // 기본 템플릿만
    statistics: false,              // 통계 X
    priority_support: false,        // 우선 지원 X
    autoBackup: false,             // 자동 백업 X
    trialDays: 14
  },
  [SUBSCRIPTION_PLANS.CARD_ONLY]: {
    maxCards: 2,                    // 🎯 명함 2장만
    maxInvoicesPerMonth: 0,         // 🚫 인보이스 기능 차단
    maxInvoicesPerYear: 0,          // 🚫 인보이스 기능 차단
    customDomain: false,            // 커스텀 도메인 X
    unlimitedQR: true,              // QR 무제한
    qrLogo: true,                   // 🎯 QR 로고 기능 ✅
    basicTemplate: true,            // 기본 템플릿만
    statistics: false,              // 🚫 통계 기능 차단
    priority_support: false,        // 우선 지원 X
    autoBackup: false,             // 자동 백업 X
    invoiceBlocked: true,          // 🚫 인보이스 완전 차단
    statisticsBlocked: true        // 🚫 통계 완전 차단
  },
  [SUBSCRIPTION_PLANS.STANDARD]: {
    maxCards: 5,                    // 명함 5장
    maxInvoicesPerMonth: 8,         // 인보이스 월간 8건
    maxInvoicesPerYear: 100,        // 인보이스 연간 100건
    customDomain: false,            // 커스텀 도메인 X
    unlimitedQR: true,              // QR 무제한
    qrLogo: true,                   // QR 로고 기능 ✅
    cardSpecialSettings: true,      // 카드별 특별 설정
    basicStatistics: true,          // 기초 통계
    priority_support: false,        // 우선 지원 X
    autoBackup: false              // 자동 백업 X
  },
  [SUBSCRIPTION_PLANS.PRO]: {
    maxCards: -1,                   // 명함 무제한
    maxInvoicesPerMonth: -1,        // 인보이스 월간 무제한
    maxInvoicesPerYear: -1,         // 인보이스 연간 무제한
    customDomain: true,             // 커스텀 도메인 ✅
    unlimitedQR: true,              // QR 무제한
    qrLogo: true,                   // QR 로고 기능 ✅
    cardAdvancedSettings: true,     // 카드별 무제한 고급 설정
    advancedStatistics: true,       // 고급 통계
    priority_support: true,         // 우선 지원 ✅
    autoBackup: true,              // 자동 백업·우선 지원 등 ✅
    customTemplates: true          // 커스텀 템플릿
  },
  [SUBSCRIPTION_PLANS.EXPIRED]: {
    maxCards: 0,                    // 모든 기능 제한
    maxInvoicesPerMonth: 0,         // 월간 제한
    maxInvoicesPerYear: 0,          // 연간 제한
    customDomain: false,
    unlimitedQR: false,
    qrLogo: false,                  // QR 로고 기능 X
    priority_support: false
  }
} as const;

interface SubscriptionContextType {
  userPlan: string;
  trialEndsAt: Date | null;
  planExpiresAt: Date | null;
  subscriptionStatus: string; // 🆕 구독 상태 추가
  isLoading: boolean;
  isPro: boolean;
  isStandard: boolean;
  isCardOnly: boolean;
  isTrialActive: boolean;
  isExpired: boolean;
  planLimits: typeof PLAN_LIMITS[keyof typeof PLAN_LIMITS];
  canCreateCard: boolean;
  canCreateInvoice: boolean;
  canUseCustomDomain: boolean;
  initializeUserSubscription: () => Promise<void>;
  updateUserPlan: (plan: string) => Promise<void>;
  checkTrialStatus: () => Promise<void>;
  refreshSubscription: () => Promise<void>; // 🆕 구독 상태 새로고침 함수 추가
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

// 안전하게 Timestamp → Date 변환
const safeTimestampToDate = (timestamp: any): Date | null => {
  if (!timestamp) return null;
  if (timestamp instanceof Date) return timestamp;
  if (timestamp && typeof timestamp.toDate === 'function') {
    return timestamp.toDate();
  }
  if (timestamp && timestamp.seconds) {
    return new Date(timestamp.seconds * 1000);
  }
  return null;
};

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [userPlan, setUserPlan] = useState<string>(SUBSCRIPTION_PLANS.FREE_TRIAL);
  const [trialEndsAt, setTrialEndsAt] = useState<Date | null>(null);
  const [planExpiresAt, setPlanExpiresAt] = useState<Date | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string>('unknown'); // 🆕 구독 상태 추가
  const [isLoading, setIsLoading] = useState(true);
  const [cardCount, setCardCount] = useState(0);
  const [invoiceCount, setInvoiceCount] = useState(0);
  const [monthlyInvoiceCount, setMonthlyInvoiceCount] = useState(0);
  const [userId, setUserId] = useState<string | null>(null);

  const auth = getAuth();

  // 계산된 값들
  const isPro = userPlan === SUBSCRIPTION_PLANS.PRO;
  const isStandard = userPlan === SUBSCRIPTION_PLANS.STANDARD;
  const isCardOnly = userPlan === SUBSCRIPTION_PLANS.CARD_ONLY;
  const planLimits = PLAN_LIMITS[userPlan as keyof typeof PLAN_LIMITS] || PLAN_LIMITS[SUBSCRIPTION_PLANS.FREE_TRIAL];
  
  // 🔄 만료 로직 개선 - 실제 만료일 기준으로 판단
  const isExpired = useMemo(() => {
    // 🆕 userId와 구독 상태가 로드되지 않았으면 중립 상태 반환
    if (!userId || isLoading) {
      console.log('⏳ [SubscriptionContext] isExpired: userId 또는 구독 상태 로딩 중, 중립 상태 반환');
      return false;
    }

    const now = new Date();
    
    // 1. 명시적으로 expired 플랜인 경우
    if (userPlan === SUBSCRIPTION_PLANS.EXPIRED) {
      console.log('🔒 [SubscriptionContext] isExpired: 명시적으로 expired 플랜');
      return true;
    }
    
    // 2. subscriptionStatus가 'expired'인 경우 (웹에서는 subscriptionStatus 필드가 없을 수 있음)
    if (subscriptionStatus === 'expired') {
      console.log('🔒 [SubscriptionContext] isExpired: subscriptionStatus가 expired');
      return true;
    }
    
    // 3. FREE_TRIAL 플랜 만료 확인
    if (userPlan === SUBSCRIPTION_PLANS.FREE_TRIAL) {
      if (trialEndsAt && now > trialEndsAt) {
        console.log('🔒 [SubscriptionContext] isExpired: FREE_TRIAL 만료됨', {
          trialEndsAt: trialEndsAt.toISOString(),
          now: now.toISOString()
        });
        return true;
      }
      // trialEndsAt이 없으면 활성 상태로 처리 (14일 무제한)
      return false;
    }
    
    // 4. 유료 플랜 만료 확인
    if ((userPlan === SUBSCRIPTION_PLANS.CARD_ONLY ||
         userPlan === SUBSCRIPTION_PLANS.STANDARD ||
         userPlan === SUBSCRIPTION_PLANS.PRO)) {
      
      // planExpiresAt이 있고 만료된 경우
      if (planExpiresAt && now > planExpiresAt) {
        console.log('🔒 [SubscriptionContext] isExpired: 유료 플랜 만료됨', {
          userPlan,
          planExpiresAt: planExpiresAt.toISOString(),
          now: now.toISOString()
        });
        return true;
      }
      
      // planExpiresAt이 없으면 활성 상태로 처리
      return false;
    }
    
    // 5. 기타 경우는 활성 상태로 처리
    return false;
  }, [userId, isLoading, userPlan, trialEndsAt, planExpiresAt, subscriptionStatus]);

  const isTrialActive = useMemo(() => {
    // 유료 플랜이면 무료 체험이 아님
    if (userPlan === SUBSCRIPTION_PLANS.CARD_ONLY || 
        userPlan === SUBSCRIPTION_PLANS.STANDARD || 
        userPlan === SUBSCRIPTION_PLANS.PRO) {
      return false;
    }
    
    // FREE_TRIAL 플랜이고 trialEndsAt이 있고 아직 만료되지 않은 경우만 true
    if (userPlan === SUBSCRIPTION_PLANS.FREE_TRIAL && trialEndsAt) {
      return new Date() < trialEndsAt;
    }
    
    return false;
  }, [userPlan, trialEndsAt]);

  const canCreateCard = useMemo(() => {
    if (!userId || isLoading) {
      return false;
    }

    if (isExpired) {
      return false;
    }

    if (isPro) {
      return true;
    }
    
    const canCreate = planLimits.maxCards === -1 || cardCount < planLimits.maxCards;
    return canCreate;
  }, [userId, isLoading, isPro, userPlan, cardCount, planLimits, isExpired]);

  const canCreateInvoice = useMemo(() => {
    if (!userId || isLoading) {
      return false;
    }

    if (isExpired) {
      return false;
    }

    if (planLimits.invoiceBlocked === true) {
      return false;
    }

    if (userPlan === SUBSCRIPTION_PLANS.CARD_ONLY) {
      return false;
    }

    if (isPro) {
      return true;
    }
    
    if (userPlan === SUBSCRIPTION_PLANS.STANDARD) {
      const monthlyLimit = planLimits.maxInvoicesPerMonth;
      if (monthlyLimit !== -1 && monthlyInvoiceCount >= monthlyLimit) {
        return false;
      }
      return true;
    }
    
    if (userPlan === SUBSCRIPTION_PLANS.FREE_TRIAL) {
      const yearlyLimit = planLimits.maxInvoicesPerYear;
      if (yearlyLimit !== -1 && invoiceCount >= yearlyLimit) {
        return false;
      }
      return true;
    }
    
    return planLimits.maxInvoicesPerYear === -1 || invoiceCount < planLimits.maxInvoicesPerYear;
  }, [userId, isLoading, userPlan, isPro, isStandard, isCardOnly, isExpired, planLimits, invoiceCount, monthlyInvoiceCount]);

  const canUseCustomDomain = useMemo(() => {
    return planLimits.customDomain === true;
  }, [planLimits]);

  // 사용자 구독 정보 초기화
  const initializeUserSubscription = useCallback(async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setIsLoading(false);
      return;
    }

    const uid = currentUser.uid;
    setUserId(uid);

    try {
      // 🆕 구독 상태 문서 경로 사용 (앱과 동일)
      const subscriptionRef = doc(db, 'users', uid, 'subscription', 'status');
      const subscriptionDoc = await getDoc(subscriptionRef);
      
      if (subscriptionDoc.exists()) {
        const subscriptionData = subscriptionDoc.data();
        console.log('🔄 [Web SubscriptionContext] 구독 데이터 로드:', subscriptionData);
        
        const plan = subscriptionData.plan || SUBSCRIPTION_PLANS.FREE_TRIAL;
        const trialEnd = safeTimestampToDate(subscriptionData.trialEndsAt);
        const planExpires = safeTimestampToDate(subscriptionData.planExpiresAt);
        const status = subscriptionData.subscriptionStatus || 'active';
        
        // 🔄 만료 상태 확인 및 자동 수정 (앱과 동일한 로직)
        const now = new Date();
        let finalPlan = plan;
        let finalStatus = status;
        
        // 1. 명시적으로 expired인 경우
        if (status === 'expired' || plan === SUBSCRIPTION_PLANS.EXPIRED) {
          finalPlan = SUBSCRIPTION_PLANS.EXPIRED;
          finalStatus = 'expired';
        }
        // 2. FREE_TRIAL 만료 확인
        else if (plan === SUBSCRIPTION_PLANS.FREE_TRIAL && trialEnd && now > trialEnd) {
          finalPlan = SUBSCRIPTION_PLANS.EXPIRED;
          finalStatus = 'expired';
        }
        // 3. 유료 플랜 만료 확인
        else if ((plan === SUBSCRIPTION_PLANS.CARD_ONLY || 
                  plan === SUBSCRIPTION_PLANS.STANDARD || 
                  plan === SUBSCRIPTION_PLANS.PRO) && 
                 planExpires && now > planExpires) {
          finalPlan = SUBSCRIPTION_PLANS.EXPIRED;
          finalStatus = 'expired';
        }
        
        // 만료 상태로 변경된 경우 Firestore 업데이트
        if (finalPlan === SUBSCRIPTION_PLANS.EXPIRED && plan !== SUBSCRIPTION_PLANS.EXPIRED) {
          await updateDoc(subscriptionRef, {
            plan: SUBSCRIPTION_PLANS.EXPIRED,
            subscriptionStatus: 'expired',
            updatedAt: Timestamp.fromDate(now),
          });
          console.log('✅ [Web SubscriptionContext] 만료 상태로 업데이트됨');
        }
        
        setUserPlan(finalPlan);
        setTrialEndsAt(trialEnd);
        setPlanExpiresAt(planExpires);
        setSubscriptionStatus(finalStatus);
        
        console.log('🔄 [Web SubscriptionContext] 구독 상태 설정 완료:', {
          plan: finalPlan,
          status: finalStatus,
          trialEnd: trialEnd?.toISOString(),
          planExpires: planExpires?.toISOString()
        });
      } else {
        // 🆕 구독 상태 문서가 없으면 새로 생성 (앱과 동일한 구조)
        const trialEndDate = new Date();
        trialEndDate.setDate(trialEndDate.getDate() + 14); // 14일 무료체험
        
        await setDoc(subscriptionRef, {
          plan: SUBSCRIPTION_PLANS.FREE_TRIAL,
          trialEndsAt: Timestamp.fromDate(trialEndDate),
          subscriptionStatus: 'active',
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        });
        
        setUserPlan(SUBSCRIPTION_PLANS.FREE_TRIAL);
        setTrialEndsAt(trialEndDate);
        setPlanExpiresAt(null);
        setSubscriptionStatus('active');
        
        console.log('✅ [Web SubscriptionContext] 새 구독 상태 문서 생성');
      }
      
      // 🆕 카드 개수 조회
      const cardsSnapshot = await getDocs(collection(db, 'users', uid, 'cards'));
      setCardCount(cardsSnapshot.size);
      
      // 🆕 인보이스 개수 조회 (모든 카드의 인보이스 합산)
      let totalInvoiceCount = 0;
      let totalMonthlyInvoiceCount = 0;
      const currentMonth = new Date().getMonth();
      const currentYear = new Date().getFullYear();
      
      for (const cardDoc of cardsSnapshot.docs) {
        const cardId = cardDoc.id;
        const invoicesSnapshot = await getDocs(collection(db, 'users', uid, 'cards', cardId, 'invoices'));
        totalInvoiceCount += invoicesSnapshot.size;
        
        // 월간 인보이스 카운트
        invoicesSnapshot.forEach(doc => {
          const invoiceData = doc.data();
          if (invoiceData.createdAt) {
            const invoiceDate = invoiceData.createdAt.toDate ? invoiceData.createdAt.toDate() : new Date(invoiceData.createdAt);
            if (invoiceDate.getMonth() === currentMonth && invoiceDate.getFullYear() === currentYear) {
              totalMonthlyInvoiceCount++;
            }
          }
        });
      }
      
      setInvoiceCount(totalInvoiceCount);
      setMonthlyInvoiceCount(totalMonthlyInvoiceCount);

    } catch (error) {
      console.error('Error initializing subscription:', error);
    } finally {
      setIsLoading(false);
    }
  }, [auth]);

  // 사용자 플랜 업데이트 (앱과 동일한 로직)
  const updateUserPlan = useCallback(async (plan: string) => {
    if (!userId) return;

    try {
      // 🆕 구독 상태 문서 경로 사용 (앱과 동일)
      const subscriptionRef = doc(db, 'users', userId, 'subscription', 'status');
      
      const updateData: any = {
        plan,
        updatedAt: Timestamp.fromDate(new Date())
      };
      
      // 🆕 subscriptionStatus도 함께 업데이트
      if (plan === SUBSCRIPTION_PLANS.EXPIRED) {
        updateData.subscriptionStatus = 'expired';
      } else {
        updateData.subscriptionStatus = 'active';
      }
      
      await updateDoc(subscriptionRef, updateData);
      setUserPlan(plan);
      setSubscriptionStatus(updateData.subscriptionStatus);
      
      console.log('✅ [Web SubscriptionContext] 플랜 업데이트 완료:', {
        plan,
        subscriptionStatus: updateData.subscriptionStatus
      });
    } catch (error) {
      console.error('❌ [Web SubscriptionContext] 플랜 업데이트 오류:', error);
    }
  }, [userId]);

  // 트라이얼 상태 확인 (앱과 동일한 로직)
  const checkTrialStatus = useCallback(async () => {
    if (!userId) return;

    try {
      // 🆕 구독 상태 문서 경로 사용 (앱과 동일)
      const subscriptionRef = doc(db, 'users', userId, 'subscription', 'status');
      const subscriptionDoc = await getDoc(subscriptionRef);
      
      if (subscriptionDoc.exists()) {
        const subscriptionData = subscriptionDoc.data();
        const plan = subscriptionData.plan || SUBSCRIPTION_PLANS.FREE_TRIAL;
        const trialEndsAtDate = safeTimestampToDate(subscriptionData.trialEndsAt);
        const planExpiresAtDate = safeTimestampToDate(subscriptionData.planExpiresAt);
        const status = subscriptionData.subscriptionStatus || 'active';
        
        // 🔄 만료 상태 확인 및 자동 수정 (앱과 동일한 로직)
        const now = new Date();
        let shouldUpdate = false;
        let updateData: any = {};
        
        // 1. FREE_TRIAL 만료 확인
        if (plan === SUBSCRIPTION_PLANS.FREE_TRIAL && trialEndsAtDate && now > trialEndsAtDate) {
          updateData.plan = SUBSCRIPTION_PLANS.EXPIRED;
          updateData.subscriptionStatus = 'expired';
          shouldUpdate = true;
          console.log('⚠️ [Web SubscriptionContext] FREE_TRIAL 만료됨');
        }
        // 2. 유료 플랜 만료 확인
        else if ((plan === SUBSCRIPTION_PLANS.CARD_ONLY || 
                  plan === SUBSCRIPTION_PLANS.STANDARD || 
                  plan === SUBSCRIPTION_PLANS.PRO) && 
                 planExpiresAtDate && now > planExpiresAtDate) {
          updateData.plan = SUBSCRIPTION_PLANS.EXPIRED;
          updateData.subscriptionStatus = 'expired';
          shouldUpdate = true;
          console.log('⚠️ [Web SubscriptionContext] 유료 플랜 만료됨');
        }
        
        if (shouldUpdate) {
          updateData.updatedAt = Timestamp.fromDate(now);
          await updateDoc(subscriptionRef, updateData);
          
          // 로컬 상태도 업데이트
          setUserPlan(updateData.plan);
          setSubscriptionStatus(updateData.subscriptionStatus);
          
          console.log('✅ [Web SubscriptionContext] 만료 상태로 업데이트 완료');
        }
      }
    } catch (error) {
      console.error('❌ [Web SubscriptionContext] 트라이얼 상태 확인 오류:', error);
    }
  }, [userId]);

  // 🆕 구독 상태 새로고침 함수 (결제 완료 후 사용)
  const refreshSubscription = useCallback(async () => {
    console.log('🔄 [Web SubscriptionContext] 구독 상태 새로고침 시작');
    try {
      await initializeUserSubscription();
      console.log('✅ [Web SubscriptionContext] 구독 상태 새로고침 완료');
    } catch (error) {
      console.error('❌ [Web SubscriptionContext] 구독 상태 새로고침 실패:', error);
    }
  }, [initializeUserSubscription]);

  // 인증 상태 변경 감지
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        initializeUserSubscription();
      } else {
        setUserId(null);
        setIsLoading(false);
      }
    });

    return unsubscribe;
  }, [auth, initializeUserSubscription]);

  // 🆕 실시간 구독 상태 리스너 추가 (결제 완료 후 즉시 반영)
  useEffect(() => {
    if (!userId) return;

    console.log('🔄 [Web SubscriptionContext] 실시간 구독 상태 리스너 설정:', userId);
    
    const subscriptionRef = doc(db, 'users', userId, 'subscription', 'status');
    const unsubscribe = onSnapshot(subscriptionRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        const subscriptionData = docSnapshot.data();
        console.log('🔄 [Web SubscriptionContext] 실시간 구독 상태 업데이트:', {
          userId,
          plan: subscriptionData.plan,
          status: subscriptionData.subscriptionStatus,
          planExpiresAt: subscriptionData.planExpiresAt?.toDate?.()?.toISOString(),
          trialEndsAt: subscriptionData.trialEndsAt?.toDate?.()?.toISOString(),
          timestamp: new Date().toISOString(),
          rawData: subscriptionData
        });
        
        const plan = subscriptionData.plan || SUBSCRIPTION_PLANS.FREE_TRIAL;
        const trialEnd = safeTimestampToDate(subscriptionData.trialEndsAt);
        const planExpires = safeTimestampToDate(subscriptionData.planExpiresAt);
        const status = subscriptionData.subscriptionStatus || 'active';
        
        setUserPlan(plan);
        setTrialEndsAt(trialEnd);
        setPlanExpiresAt(planExpires);
        setSubscriptionStatus(status);
        
        console.log('✅ [Web SubscriptionContext] 실시간 상태 업데이트 완료:', {
          userId,
          plan,
          status,
          trialEnd: trialEnd?.toISOString(),
          planExpires: planExpires?.toISOString(),
          timestamp: new Date().toISOString()
        });
      } else {
        console.log('📄 [Web SubscriptionContext] 구독 상태 문서가 없음:', userId);
      }
    }, (error) => {
      console.error('❌ [Web SubscriptionContext] 실시간 리스너 오류:', {
        userId,
        error: error.message,
        timestamp: new Date().toISOString()
      });
    });

    return unsubscribe;
  }, [userId]);

  // 무료체험 상태 확인 (초기화 후)
  useEffect(() => {
    if (userId && !isLoading) {
      checkTrialStatus();
    }
  }, [userId, isLoading, checkTrialStatus]);

  // 🆕 디버깅용 로그 - 구독 상태 변경 감지
  useEffect(() => {
    console.log('🔍 [Web SubscriptionContext] 구독 상태 변경 감지:', {
      userPlan,
      subscriptionStatus,
      isExpired,
      trialEndsAt: trialEndsAt?.toISOString(),
      planExpiresAt: planExpiresAt?.toISOString(),
      isLoading,
      userId
    });
  }, [userPlan, subscriptionStatus, isExpired, trialEndsAt, planExpiresAt, isLoading, userId]);

  const value: SubscriptionContextType = {
    userPlan,
    trialEndsAt,
    planExpiresAt,
    subscriptionStatus, // 🆕 구독 상태 추가
    isLoading,
    isPro,
    isStandard,
    isCardOnly,
    isTrialActive,
    isExpired,
    planLimits,
    canCreateCard,
    canCreateInvoice,
    canUseCustomDomain,
    initializeUserSubscription,
    updateUserPlan,
    checkTrialStatus,
    refreshSubscription, // 🆕 구독 상태 새로고침 함수 추가
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};



